import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle2, AlertTriangle, ChevronRight, Info } from "lucide-react";
import { rincianBiodataKurang, FILTER_BIODATA_SEMUA, FILTER_BIODATA_IDENTITAS } from "@/lib/biodataWajib";
import { barisPengingatRotasi } from "@/lib/pengingatRotasi";

// Panel-panel Dashboard yg memakai definisi angka "satu sumber" dari compute_summary
// (server.py): semua angka = peserta Aktif, kecuali diagram Status Keaktifan (semua
// status = "terdaftar"). Kelas warna yg dipakai di sini sudah punya padanan tema gelap
// di index.css (text-slate-*, border-slate-200, bg-*-50, text-emerald/amber-700).

export const angka = (n) => (n ?? 0).toLocaleString("id-ID");

export function persen(bagian, total) {
    if (!total) return "0%";
    return `${((bagian / total) * 100).toLocaleString("id-ID", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

// Ikon (i) di kartu "Peserta Aktif" -- Popover (klik/tap), bukan tooltip hover, supaya
// juga bisa dibuka di ponsel.
export function InfoDefinisiAktif() {
    return (
        <Popover>
            <PopoverTrigger
                type="button"
                aria-label="Penjelasan definisi Peserta Aktif"
                className="inline-flex align-middle text-slate-400 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B9AD] rounded-full"
                data-testid="kpi-info-aktif"
            >
                <Info className="w-3.5 h-3.5" />
            </PopoverTrigger>
            <PopoverContent className="text-xs leading-relaxed normal-case tracking-normal font-normal" align="start">
                <p className="font-semibold text-sm mb-1">Peserta Aktif</p>
                <p>Peserta berstatus <b>Aktif</b>. Semua kartu, grafik, Tahap Sp1, dan sebaran
                    Semester di Dashboard menghitung peserta Aktif saja.</p>
                <p className="mt-2"><b>Terdaftar</b> = semua peserta yang belum dihapus, apa pun
                    statusnya (Aktif, Cuti, Lulus, Tidak Aktif, Meninggal) — dipakai diagram
                    Status Keaktifan.</p>
            </PopoverContent>
        </Popover>
    );
}

// Daftar angka + persen di bawah diagram Status Keaktifan (menggantikan label irisan
// yg bertumpuk bila irisannya kecil).
export function StatusLegend({ rows, colors, terdaftar }) {
    return (
        <ul className="space-y-1.5" data-testid="status-legend">
            {rows.map((s) => (
                <li key={s.label} className="flex items-center gap-2 text-sm">
                    {/* Cincin tipis abu: titik gelap (Meninggal/Tidak Aktif) tetap terlihat di tema gelap. */}
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-slate-400/60" style={{ background: colors[s.label] || "#94A3B8" }} aria-hidden="true" />
                    <span className="flex-1 text-slate-700">{s.label}</span>
                    <span className="font-bold text-slate-900 tabular-nums">{angka(s.count)}</span>
                    <span className="w-14 text-right text-xs text-slate-500 tabular-nums">{persen(s.count, terdaftar)}</span>
                </li>
            ))}
            <li className="flex items-center gap-2 text-xs text-slate-500 border-t border-slate-200 pt-1.5">
                <span className="flex-1">Total terdaftar</span>
                <span className="font-bold text-slate-700 tabular-nums">{angka(terdaftar)}</span>
                <span className="w-14 text-right tabular-nums">100%</span>
            </li>
        </ul>
    );
}

function BarisCek({ ok, judul, rumus }) {
    const Icon = ok ? CheckCircle2 : AlertTriangle;
    return (
        <li className="flex items-start gap-2">
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-700" : "text-amber-700"}`} aria-hidden="true" />
            <div className="min-w-0">
                <div className="text-slate-700">{judul} <span className="sr-only">{ok ? "(cocok)" : "(tidak cocok)"}</span></div>
                <div className="text-xs text-slate-500 tabular-nums break-words">{rumus}</div>
            </div>
        </li>
    );
}

// Kotak "Cek angka": menampilkan identitas yg dijamin compute_summary supaya pembaca
// bisa mencocokkan sendiri angka di kartu.
export function CekAngka({ summary }) {
    const { aktif, sp1_aktif: sp1, sp2_aktif: sp2, koas_aktif: koas, stase } = summary;
    const totalStase = Object.values(stase).reduce((a, b) => a + b, 0);
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="cek-angka">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Cek angka</div>
            <ul className="space-y-2 text-sm">
                <BarisCek
                    ok={aktif === sp1 + sp2 + koas}
                    judul="Aktif = SP1 + SP2 + Koas"
                    rumus={`${angka(aktif)} = ${angka(sp1)} + ${angka(sp2)} + ${angka(koas)}`}
                />
                <BarisCek
                    ok={aktif === totalStase}
                    judul="Total stase = Aktif"
                    rumus={`${angka(stase.rscm)} + ${angka(stase.luar)} + ${angka(stase.mdu_mdk)} + ${angka(stase.penelitian)} + ${angka(stase.belum_stase ?? 0)} + ${angka(stase.belum_diisi)} = ${angka(totalStase)}`}
                />
            </ul>
        </div>
    );
}

function BarisPerlu({ label, jumlah, to, testId, rincian }) {
    const beres = !jumlah;
    return (
        <li>
            <Link
                to={to}
                data-testid={testId}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 border border-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B9AD]"
            >
                {beres
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" aria-hidden="true" />
                    : <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" aria-hidden="true" />}
                <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-700">{label}</span>
                    {!beres && rincian && (
                        <span className="block text-xs text-slate-500 mt-0.5" data-testid={`${testId}-rincian`}>{rincian}</span>
                    )}
                </span>
                {beres
                    ? <span className="text-sm font-semibold text-emerald-700">Semua terisi</span>
                    : <span className="text-sm font-bold text-slate-900 tabular-nums">{angka(jumlah)} peserta</span>}
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
            </Link>
        </li>
    );
}

// Kartu "Perlu dilengkapi" (hanya mode data terkini). Tiap baris menuju daftar yg
// angkanya identik: Daftar Peserta dgn filter yg sama, atau Rotasi Klinik bulan itu
// (bulan lalu yg masih ada belum isi + bulan berjalan, lihat lib/pengingatRotasi.js).
export function PerluDilengkapi({ data, showSp1Tahap }) {
    return (
        <Card className="border-slate-200 shadow-sm" data-testid="perlu-dilengkapi">
            <CardContent className="p-6">
                <h3 className="font-bold text-slate-800">Perlu dilengkapi</h3>
                <p className="text-xs text-slate-500 mb-3">Peserta aktif dengan data yang belum lengkap — klik untuk melihat daftarnya</p>
                <ul className="space-y-2">
                    <BarisPerlu label="Semester / Periode Masuk belum diisi" jumlah={data.semester_kosong}
                        to="/peserta?status=Aktif&belum_diisi=1" testId="perlu-semester" />
                    <BarisPerlu label="Stase belum diisi" jumlah={data.tanpa_stase}
                        to="/peserta?status=Aktif&stase=__tanpa" testId="perlu-stase" />
                    {showSp1Tahap && (
                        <BarisPerlu label="Sp1 tanpa Tahap" jumlah={data.sp1_tanpa_tahap}
                            to="/peserta?status=Aktif&jenjang=Sp1&tahap=__kosong" testId="perlu-tahap" />
                    )}
                    {data.biodata_kurang !== undefined && (
                        <BarisPerlu label="Biodata wajib belum lengkap" jumlah={data.biodata_kurang}
                            rincian={rincianBiodataKurang(data.biodata_kurang_per_field)
                                .map((b) => `${b.label} ${angka(b.jumlah)}`).join(" · ")}
                            to={`/peserta?status=Aktif&biodata=${FILTER_BIODATA_SEMUA}`} testId="perlu-biodata" />
                    )}
                    {data.identitas_kurang !== undefined && (
                        <BarisPerlu label="NIK / Email belum diisi" jumlah={data.identitas_kurang}
                            to={`/peserta?status=Aktif&biodata=${FILTER_BIODATA_IDENTITAS}`} testId="perlu-identitas" />
                    )}
                    {barisPengingatRotasi(data).map((b) => (
                        <BarisPerlu key={b.key} label={b.label} jumlah={b.jumlah} to={b.to} testId={b.testId} />
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}

// Sebaran Semester peserta aktif (dipakai Admin Prodi menggantikan grafik per prodi
// yg cuma berisi satu batang). Empat kelompok selalu berjumlah = Aktif.
export function SebaranSemester({ rows, aktif, barColor }) {
    const maks = Math.max(1, ...rows.map((r) => r.count));
    return (
        <Card className="border-slate-200 shadow-sm" data-testid="sebaran-semester">
            <CardContent className="p-6">
                <h3 className="font-bold text-slate-800">Sebaran Semester</h3>
                <p className="text-xs text-slate-500 mb-4">Peserta aktif prodi Anda</p>
                <ul className="space-y-3">
                    {rows.map((r) => (
                        <li key={r.key}>
                            <div className="flex items-center gap-2 text-sm mb-1">
                                <span className="flex-1 text-slate-700">{r.label}</span>
                                <span className="font-bold text-slate-900 tabular-nums">{angka(r.count)}</span>
                                <span className="w-14 text-right text-xs text-slate-500 tabular-nums">{persen(r.count, aktif)}</span>
                            </div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        width: `${(r.count / maks) * 100}%`,
                                        background: r.key === "belum_diisi" ? "hsl(var(--muted-foreground))" : barColor,
                                    }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between text-xs text-slate-500">
                    <span>Total (= Peserta Aktif)</span>
                    <span className="font-bold text-slate-700 tabular-nums">{angka(rows.reduce((a, r) => a + r.count, 0))}</span>
                </div>
            </CardContent>
        </Card>
    );
}
